import React, { useEffect, useCallback, useState } from 'react'
import styled from 'styled-components'
import EditIcon from '@mui/icons-material/EditOutlined'

import { Panel } from 'components/Panel'
import ColumnGridPanel from 'components/ColumnGridPanel'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Textarea from 'components/Textarea'

import GA4 from 'helpers/ga4'
import { scrubText } from 'helpers/inputCleaners'

import Api from 'services/api'

import { Device } from 'types/Device'

import { NoData, PanelHeaderPrimaryButton, PanelHeaderSecondaryButton } from 'styles/shared'
import { color } from 'styles/colors'

type props = {
  device: Device
  refreshPageCallback?: () => void | null | undefined
}

const NotesPanel = ({ device, refreshPageCallback }: props) => {
  const [rows, setRows] = useState(5)
  const [deviceNote, setDeviceNote] = useState(device.note)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  useEffect(() => {
    if (deviceNote) {
      const notesArray = deviceNote.split('\n')

      if (notesArray.length > rows) {
        setRows(notesArray.length)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceNote])

  const onEditClick = () => {
    setIsEditing(true)

    GA4.sendEvent({ category: 'Customer Detail Page', action: 'Click Device Details Edit Button', label: 'Customer Notes' })
  }

  const onCancelClick = () => {
    setIsEditing(false)
    setDeviceNote(device.note)
  }

  const onSaveClick = useCallback(async () => {
    if (deviceNote === device.note) {
      setIsEditing(false)
    } else {
      GA4.sendEvent({ category: 'Customer Detail Page', action: 'Click Save Device Note' })

      setIsSaving(true)
      const note = deviceNote && deviceNote.length > 0 ? deviceNote.trim() : deviceNote
      const editDevice: Pick<Device, 'note'> = { note }

      await Api.Device.update(device.dealerId, device.deviceId, editDevice)
        .then(() => {
          setIsSaving(false)
          setIsEditing(false)
          setDeviceNote(note)
          device.note = note
          refreshPageCallback && refreshPageCallback()
        })
    }
  }, [device, deviceNote, refreshPageCallback])

  return (
    <S.Panel pingdomId="notes-container" id="notes" scrollToSection>
      <S.PanelHeader>
        <S.PanelHeaderLeft>
          <Panel.Title>Notes</Panel.Title>
          {!isEditing
            ? <Button decoration="primary" styles={PanelHeaderSecondaryButton} onPress={onEditClick}><S.ButtonText><S.EditIcon /><span>Edit</span></S.ButtonText></Button>
            : (
              <>
                <Button decoration="primary" styles={PanelHeaderSecondaryButton} text="Cancel" onPress={onCancelClick} />
                <Button enabled={!isSaving} decoration="positive" styles={PanelHeaderPrimaryButton} text="Save" onPress={onSaveClick} />
              </>
            )
          }
        </S.PanelHeaderLeft>
      </S.PanelHeader>
      {!deviceNote && !isEditing
        ? <S.NoData><h1>Add notes here</h1></S.NoData>
        : (
          <S.ColumnGridPanel>
            {isEditing
              ? (
                <S.Editor>
                  <S.Textarea autoFocus placeholder="Add notes here" rows={rows} isRequired={false} label="" value={deviceNote ?? ''} onChange={event => setDeviceNote(scrubText(event.target.value, true))} />
                </S.Editor>
              )
              : <S.NoteText>{deviceNote}</S.NoteText>
            }
          </S.ColumnGridPanel>
        )
      }
    </S.Panel>
  )
}

export default NotesPanel

const S = {
  NoData: styled(NoData)`
    h1 {
      font-size: 28px;
    }
  `,
  PanelHeaderLeft: styled(Panel.Header.Left)`
   display: flex;
   align-items: center;
 `,
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
  `,
  PanelHeader: styled(Panel.Header)`
    padding-right: 4px;
  `,
  ColumnGridPanel: styled(ColumnGridPanel)`
    background-color: ${color('paneBackground')};
  `,
  NoteText: styled.p`
    padding: 0px 10px;
    white-space: pre-line;
    word-wrap: break-word;
  `,
  Editor: styled.div`
    padding: 0px 10px;
  `,
  Textarea: styled(Textarea)`
    padding:10px 0px 0px 0px;
    display: block;
    width: 100%;
    overflow: hidden;
    resize: vertical;
    min-height: 40px;
    height: auto;
    form-sizing: content;
  `,
  ErrorMessage: styled.span`
    color: ${color('errorMessage')};
    font-weight: 600;
  `,
  ButtonText: styled.div`
   color: ${color('primaryButton')};
   font-weight: 700;
   font-size: 16px;
   display: flex;
   align-items: center;
   gap: 5px;
   &:hover {
     color: ${color('primaryButtonHover')};
   }
 `,
  EditIcon: styled(EditIcon)`
   font-size: 1.25em;
 `
}
